import React, { useEffect, useState } from 'react';
import useAxios from 'axios-hooks';
import { PrimaryButton } from '@fluentui/react';
import { Form, Formik } from 'formik';
import { useParams, useHistory } from 'react-router-dom';
import Select from "react-select";
import { FixedSizeList as List } from "react-window";
import Toast from '../../../Forms/Toast';
import FeatureSummary from '../../../Util/FeatureSummary';

const newCobrandEmailSenderObject = {
    cobrandEmailSenderId: 0,
    cobrandId: null,
    emlSenderId: null,
};

const CustomMenuList = ({ children, ...props }) => {
    const height = 35;
    const itemCount = children.length;
    const totalHeight = Math.min(300, itemCount * height);

    return (
        <List height={totalHeight} itemCount={itemCount} itemSize={height} width="100%">
            {({ index, style }) => (
                <div
                    style={{
                        ...style,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        padding: "0 8px",
                        boxSizing: "border-box",
                    }}
                    title={children[index]?.props?.children}
                >
                    {children[index]}
                </div>
            )}
        </List>
    );
};

const WizardPage = () => {
    const [showToast, setShowToast] = useState(false);
    const [showError, setShowError] = useState(false);
    const history = useHistory();
    const { cobrandEmailSenderId: idFromParams } = useParams();

    const [{ data: cobrandEmailSender }, getCobrandEmailSender] = useAxios(
        { url: `/emails/cobrand-email-sender/${idFromParams}`, method: 'GET' },
        { manual: true }
    );

    const [{ data: cobrands, loading: loadingCobrands }] = useAxios('/internal/cobrands');
    const [{ data: senders, loading: loadingSenders }] = useAxios('/emails/email-senders');

    const cobrandsOptions = cobrands?.map(u => ({ value: u.cobrandId, label: u.name })) || [];
    const sendersOptions = senders?.map(s => ({ value: s.emlSenderID, label: `${s.fromName} - ${s.fromEmailAddress}` })) || [];

    useEffect(() => {
        if (idFromParams) {
            getCobrandEmailSender();
        }
    }, [idFromParams]);

    const [{ loading: submitting }, submitForm] = useAxios(
        { url: `/emails/cobrand-email-sender` },
        { manual: true }
    );

    return (
        <Formik
            initialValues={cobrandEmailSender || newCobrandEmailSenderObject}
            onSubmit={async (values) => {
                setShowError(false);
                const isUpdate = !!idFromParams;
                const result = await submitForm({
                    url: "/emails/cobrand-email-sender",
                    method: isUpdate ? "PUT" : "POST",
                    data: values,
                });

                if (!result.isAxiosError) {
                    if (!idFromParams && result.data?.cobrandEmailSenderId) {
                        history.push(`/cobrand-email-senders-feature/${result.data.cobrandEmailSenderId}/wizard`);
                    }
                    setShowToast(true);
                } else {
                    setShowError(true);
                }
            }}
            enableReinitialize
            isInitialValid={false}
        >
            {({ values, setFieldValue, handleSubmit, dirty }) => {
                const isButtonDisabled = !dirty || !values.cobrandId || !values.emlSenderId;

                return (
                    <Form style={{ maxWidth: "500px", marginBottom: 500 }}>
                        <div className="displayflex mb20" style={{ justifyContent: 'space-between' }}>
                            <h4>Cobrand Email Sender</h4>
                            <PrimaryButton
                                onClick={handleSubmit}
                                text={idFromParams ? "Update" : 'Create'}
                                disabled={isButtonDisabled}
                            />
                        </div>

                        {showError && (
                            <div style={{ color: 'rgb(164, 38, 44)' }}>
                                An error has occurred.
                            </div>
                        )}

                        <div className='panel-h' style={{ fontWeight: '500', marginBottom: 5 }}>Select Cobrand</div>
                        <Select
                            components={{ MenuList: CustomMenuList }}
                            options={cobrandsOptions}
                            placeholder="Select Cobrand"
                            name="cobrandId"
                            value={cobrandsOptions.find(option => option.value === values.cobrandId)}
                            onChange={(option) => {
                                setFieldValue('cobrandId', option.value);
                            }}
                            isSearchable
                            isLoading={loadingCobrands}
                            styles={{
                                container: base => ({ ...base, width: 500 }),
                                control: base => ({ ...base, border: "1px solid #aaaaaa" }),
                                menuList: base => ({ ...base, maxHeight: "300px", overflowY: "auto" }),
                            }}
                        />

                        <div className='panel-h' style={{ fontWeight: '500', marginBottom: 5, marginTop: 20 }}>Select Email Sender</div>
                        <Select
                            components={{ MenuList: CustomMenuList }}
                            options={sendersOptions}
                            placeholder="Select Email Sender"
                            name="emlSenderId"
                            value={sendersOptions.find(option => option.value === values.emlSenderId)}
                            onChange={(option) => {
                                setFieldValue('emlSenderId', option.value);
                            }}
                            isSearchable
                            isLoading={loadingSenders}
                            styles={{
                                container: base => ({ ...base, width: 500 }),
                                control: base => ({ ...base, border: "1px solid #aaaaaa" }),
                                menuList: base => ({ ...base, maxHeight: "300px", overflowY: "auto" }),
                            }}
                        />

                        <Toast show={showToast} message="Saved!" onClose={() => setShowToast(false)} />
                        <FeatureSummary
                            label="About Cobrand Email Senders"
                            summary="Link a cobrand to a specific email sender configuration. This determines which 'from' name, email address, and physical address appears on Sendgrid emails sent to users of that cobrand."
                        />
                    </Form>
                );
            }}
        </Formik>
    );
};

export default WizardPage;
